import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Appraisal, EmployeeAssesment, Objective, OKR, Quarter } from 'src/app/shared/models/appraisal';
import { Context } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { ApparaisalService } from 'src/app/shared/services/apparaisal.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { RegExp } from 'src/app/core/config/regex';
@Component({
  selector: 'app-appilication-crud',
  templateUrl: './appilication-crud.component.html',
  styleUrls: ['./appilication-crud.component.scss']
})
export class AppilicationCrudComponent implements OnInit {
  @Input() appraisal!: Appraisal;
  public searchKeyword: string = '';
  public submitted: boolean = false;
  public okrForm!: FormGroup;
  public currentTab: number = 0;
  public context!: Context;
  public isObjectiveEnabled: boolean = false;
  public employeeAssesment: EmployeeAssesment;
  public okrStatus: boolean;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private appraisalService: ApparaisalService,
    private toastr: ToastrService,
    private authService: AuthService,
    private commonService: CommonService,
  ) { }

  ngOnInit(): void {
    this.context = this.authService.getContextData();
    this.getEmployeeAssessment();
    this.setOkrForm([]);
    if (this.appraisal?.id) {
      this.getQuarters(this.appraisal.id);
      this.checkObjectiveStatus(this.appraisal.id);
    }
  }

  public getEmployeeAssessment(): void {
    this.appraisalService.getAssessment(this.appraisal.id, this.context.employeeId)
      .subscribe({
        next: (employeeAssesment) => {
          this.employeeAssesment = employeeAssesment;
          this.okrStatus = ['APPRAISEE_SUBMITTED', 'LONE_COMPLETED', 'HR_COMPLETED'].includes(employeeAssesment.status);
        },
        error: (error) => {
        }
      });
  }
  

  private setOkrForm(quarters: Quarter[] = []) {
    this.okrForm = this.fb.group({
      quarters: this.fb.array(quarters.length ? quarters.map(q => this.createQuarter({ quarterId: q.id, quarterName: q.label } as OKR)) : []),
    });
  }

  private createQuarter(okr: OKR) {
    return this.fb.group({
      quarterId: this.fb.control(okr.quarterId, [Validators.required]),
      quarterName: this.fb.control(okr.quarterName),
      objectives: this.fb.array(okr?.objectives ? okr?.objectives?.map(o => this.createObjective(o)) : [this.createObjective(undefined, okr)]),
    });
  }

  private createObjective(objective?: Objective, okr?: OKR) {
    return this.fb.group({
      id: this.fb.control(objective?.id || null, []),
      objective: this.fb.control(objective?.objective || '', [Validators.required]),
      keyResult: this.fb.control(objective?.keyResult || '', [Validators.required]),
      selfRating: this.fb.control(objective?.selfRating || '', [Validators.pattern(RegExp.selfRating)]),
      comments: this.fb.control(objective?.comments || '', []),
      year: this.fb.control(objective?.year, []),
      appraiseeId: this.fb.control(objective?.appraiseeId || this.context?.employeeId, [Validators.required]),
      appraiseeName: this.fb.control(objective?.appraisalName),
      quarterId: this.fb.control(objective?.quarterId || okr.quarterId, [Validators.required]),
      quarterName: this.fb.control(objective?.quarterName),
      appraisalId: this.fb.control(objective?.appraisalId || this.appraisal?.id, [Validators.required]),
      appraisalName: this.fb.control(objective?.appraisalName),
    })
  }

  get quarters(): FormArray {
    return this.okrForm.get("quarters") as FormArray;
  }

  public objectives(i: number): FormArray {
    return this.quarters?.at(i)?.get("objectives") as FormArray;
  }

  private getQuarters(appraisalId: number) {
    this.appraisalService.getQuarters(appraisalId).subscribe({
      next: (quarters: Quarter[]) => {
        quarters = quarters.sort((a, b) => a.sequence - b.sequence);        
        this.currentTab = quarters[0]?.id ?? 0;
        this.setOkrForm(quarters);
        this.getObjectives(this.appraisal.id);
      },
      error: (error: HttpErrorResponse) => {
        this.toastr.error(error?.error?.error || error.error);
      }
    });
  }

  public addObjective(quarterIndex: number, objective: FormGroup): void {
    objective.markAllAsTouched();
    if (this.objectives(quarterIndex).valid) {
      this.objectives(quarterIndex).push(this.createObjective(undefined, objective.value));
    }
  }

  public deleteObjective(quarterIndex: number, objectiveIndex: number, objective: FormGroup): void {
    this.objectives(quarterIndex).removeAt(objectiveIndex);
    if (!objective.value.id) {
      this.objectives(quarterIndex).removeAt(objectiveIndex);
      this.commonService.setToastr(200, 'Objective deleted successfully');
    } else {
      this.appraisalService.deleteObjective(objective.value.id).subscribe({
        next: (message: any) => {
          if (message.text) {
            this.commonService.setToastr(200, message.text);
          } else {
            this.commonService.setToastr(500, 'Something went wrong! Please try again.');
          }
        },
        error: (error: any) => {
          this.commonService.setToastr(error.status, error?.error?.response || error.message);
        }
      });
    }
  }

  private checkObjectiveStatus(appraisalId: number) {
    this.appraisalService.checkObjectiveStatus(appraisalId).subscribe({
      next: (objective: Message) => {
        this.isObjectiveEnabled = !objective.failed;
      },
      error: (error: HttpErrorResponse) => {
        this.toastr.error(error?.error?.error || error.error);
      }
    });
  }

  public saveObjective(inProgress: any, submitted?: boolean) {
    const currentQuarterIndex = this.quarters.value.findIndex(q => q.quarterId === this.currentTab);
    const currentQuarter = this.quarters.at(currentQuarterIndex);
    const objectives = currentQuarter.get('objectives').value;
    currentQuarter.markAllAsTouched();
    if(currentQuarter.valid){
      if (submitted && this.isObjectiveEnabled) {
        if (objectives.some(o => !o.selfRating || !o.comments)) {
          this.toastr.error('Please enter a valid self-rating and comments to submit your objective.');
          return;
        }
      }
      inProgress.value = true;
      this.appraisalService.createObjective(objectives).subscribe({
        next: (objective: Message) => {
          if (objective.code === 200) {
               this.quarters.value?.forEach((q, i) => {
              if (this.objectives(i).value.some(o => o.objective || o.keyResult || o.selfRating || o.comments)) {
                this.objectives(i).clear();
              }
            });
              this.getObjectives(this.appraisal.id);
              if (submitted) {
                this.submitObjective(inProgress);
              }
            }
          this.commonService.setToastr(200, objective.text);
          inProgress.value = false;
        },
        error: (error: HttpErrorResponse) => {
          this.toastr.error(error?.error?.error || error.error);
          inProgress.value = false;
        }
      });
    } else {
      this.toastr.error('Please enter the valid objective details');
    }
  }

  private getObjectives(appraisalId: number) {
    this.appraisalService.getObjectives(appraisalId).subscribe({
      next: (objectives: Objective[]) => {
        if (objectives?.length > 0) {
          objectives?.forEach(o => {
            const index = this.quarters.value.findIndex(q => q.quarterId === o.quarterId);
            if (index != -1) {
              this.objectives(index).push(this.createObjective(o));
              this.removeEmptyControls(index)
            }
          });
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastr.error(error?.error?.error || error.error);
      }
    });
  }

  private removeEmptyControls(index: number): void {
    let controlsToRemove: number[] = [];
    this.objectives(index).controls.forEach((control, index) => {
      if (!control.valid) {
        controlsToRemove.push(index);
      }
    });
    for (let i = controlsToRemove.length - 1; i >= 0; i--) {
      this.objectives(index).removeAt(controlsToRemove[i]);
    }
  }

  public submitObjective(inProgress?:any) {
    this.appraisalService.submitObjective(this.appraisal.id).subscribe({
      next: (objective: any) => {
        inProgress.value=false;
        this.activeModal.close(objective);
        this.commonService.setToastr(200, objective.text);
      },
      // error: (error: HttpErrorResponse) => {
      //   this.toastr.error(error?.error?.error || error.error);
      // }
    });
  }
}
